// Clinging film follows the filling; spare border folds stretch much less.
export function pouchRetention(point,body,film){
  let edge=0;
  for(let a=0;a<2;a++){
    const center=(body.min[a]+body.max[a])*.5,upper=point[a]>=center;
    const start=upper?body.max[a]:body.min[a],end=upper?film.max[a]:film.min[a];
    const t=Math.max(0,Math.min(1,(point[a]-start)/(end-start||.0001)));
    edge=Math.max(edge,t*t*(3-2*t));
  }
  return 1-.85*edge;
}

// A light secondary field gives film a bounded lag without another solver.
export class PouchSkin {
  constructor(body,film=null){
    this.body=body;this.initialRest=body.initialRest;this.min=body.min;this.max=body.max;this.anchorBottom=body.anchorBottom;
    this.p=body.initialRest.slice();this.activity=0;
    this.filling={initialRest:this.initialRest,min:this.min,max:this.max,anchorBottom:this.anchorBottom,p:body.initialRest.slice()};
    this.center=this.min.map((v,a)=>(v+this.max[a])*.5);
    this.spread=[0,1].map(a=>film?Math.max(0,Math.min(.55,(film.max[a]-film.min[a])/(this.max[a]-this.min[a])*.95-1)):0);
    this.focus=[...this.center];this.radius=[0,1].map(a=>(this.max[a]-this.min[a])*.41);
    this.film=film;
    this.pressure=0;this.stuck=0;this.scales=[1,1,1];this.flowLimit=1;
  }
  reset(){this.p.set(this.initialRest);this.filling.p.set(this.initialRest);this.activity=0;this.pressure=0;this.stuck=0;this.scales=[1,1,1];this.flowLimit=1;this.focus=[...this.center];}
  fromBody(point,out=[0,0,0]){
    const area=this.flow(point,out);
    out[2]=this.center[2]+(point[2]-this.center[2])/area;
    return out;
  }
  flow(point,out){
    const dx=point[0]-this.focus[0],dy=point[1]-this.focus[1];
    const weight=Math.exp(-((dx/this.radius[0])**2+(dy/this.radius[1])**2));
    const gradient=[-2*dx/this.radius[0]**2,-2*dy/this.radius[1]**2],jacobian=[1,0,0,1];
    for(let a=0;a<2;a++){
      const strain=(this.scales[a]-1)*weight;
      const displacement=(point[a]-this.focus[a])*strain;
      for(let b=0;b<2;b++)jacobian[a*2+b]+=strain*((a===b?1:0)+(point[a]-this.focus[a])*gradient[b]);
      out[a]=point[a]+displacement;
    }
    // The actual XY area includes the spatial falloff. A hard seal limiter
    // would make the area vanish and create a spike in the returned thickness.
    // Its inverse thickens the surrounding rim as the contact becomes thin.
    return Math.max(.35,jacobian[0]*jacobian[3]-jacobian[1]*jacobian[2]);
  }
  toBody(point){
    // Invert the smooth local flow for a new contact. Pointer deltas use a
    // snapshot of this mapping, so animation cannot feed back into input.
    const guess=[...point],sample=[0,0,0];
    for(let i=0;i<32;i++){
      this.fromBody(guess,sample);
      for(let a=0;a<2;a++)guess[a]+=(point[a]-sample[a])*.7;
    }
    const thickness=this.flow(guess,sample);
    guess[2]=this.center[2]+(point[2]-this.center[2])*thickness;
    return guess;
  }
  step(elapsed){
    if(!Number.isFinite(elapsed)||elapsed<=0)return;
    const dt=Math.min(elapsed,1/30),follow=1-Math.exp(-dt/0.07);
    const hold=this.body.hold;
    // The bag may be turned over. Compression points inward from either face;
    // pulling its rear face outward must not be interpreted as a front press.
    const contactSide=hold?(Math.sign(hold.start[2]-this.center[2])||1):1;
    const targetPressure=hold?Math.max(0,Math.min(1,contactSide*(hold.start[2]-hold.target[2])/Math.min(.58,(this.max[2]-this.min[2])*.55))):0;
    const previousPressure=this.pressure;
    if(hold)for(let a=0;a<2;a++){
      const target=Math.max(this.min[a],Math.min(this.max[a],hold.target[a]));
      this.focus[a]+=(target-this.focus[a])*(1-Math.exp(-dt/.07));
    }
    // Tack: the longer a firm press holds, the more the filling clings flat to the
    // film. Letting go releases slowly while it clings, then peels away fast.
    const peeling=this.pressure<=.28;
    if(hold&&targetPressure>.05)this.stuck+=(1-this.stuck)*(1-Math.exp(-dt*2*this.pressure));
    else if(hold)this.stuck*=Math.exp(-dt/.5);
    else this.stuck*=Math.exp(-dt/(peeling?.12:4));
    if(this.stuck<.001)this.stuck=0;
    const settle=targetPressure>this.pressure?.08:peeling?.11:.22+.85*this.stuck;
    this.pressure+=(targetPressure-this.pressure)*(1-Math.exp(-dt/settle));
    if(this.pressure<.0001&&targetPressure===0)this.pressure=0;
    // Only the thumb's neighborhood spreads and thins. Distant filling keeps
    // its thickness and the unused bag border stays slack. XPBD supplies drag.
    let activity=Math.abs(this.pressure-previousPressure)/dt;
    const point=[0,0,0],deformed=[0,0,0];
    this.flowLimit=Math.min(1,this.flowLimit+dt*.5);
    const buildField=()=>{
      for(let a=0;a<2;a++)this.scales[a]=1+this.spread[a]*(1.3+.5*this.stuck)*this.pressure*this.flowLimit;
      this.scales[2]=1/(this.scales[0]*this.scales[1]);
      for(let i=0;i<this.p.length;i+=3){
        for(let a=0;a<3;a++)point[a]=this.body.p[i+a];
        this.fromBody(point,deformed);
        for(let a=0;a<3;a++)this.filling.p[i+a]=deformed[a];
      }
    };
    const valid=field=>this.body.tets.every(t=>this.body.volume(field,t.v)>=Math.min(t.rest,this.body.volume(this.body.p,t.v))*.12);
    buildField();
    // Even an orientation-preserving continuous field can fold a coarse,
    // already-bent cage. Limit that additional strain before transporting it.
    for(let attempt=0;attempt<12&&!valid(this.filling.p);attempt++){this.flowLimit*=.5;buildField();}
    if(!valid(this.filling.p)){this.flowLimit=0;buildField();}
    for(let i=0;i<this.p.length;i+=3){
      for(let a=0;a<3;a++){
        const k=i+a,target=this.filling.p[k];
        const previous=this.p[k],next=previous+(target-previous)*follow;
        // Keep the delayed membrane within the thin authored contact gap.
        // A larger lag can pass through the filling as a pressed rim rebounds.
        this.p[k]=Math.max(target-.003,Math.min(target+.003,next));
        activity=Math.max(activity,Math.abs(this.p[k]-previous)/dt,Math.abs(target-this.p[k]));
      }
    }
    if(!valid(this.p))this.p.set(this.filling.p);
    if(activity<.0003&&!this.body.hold&&this.body.activity===0){this.p.set(this.initialRest);activity=0;}
    this.activity=activity;
  }
}
